import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  Box,
  DialogTitle,
  DialogContent,
  Grid,
  FormLabel,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Input,
  DialogActions,
  Checkbox,
  FormHelperText,
  OutlinedInput,
  ListItemText,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { axiosInstance } from "../../utils/tokenSetCookie";
import { toast } from "react-toastify";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

import {
  LocationClassList,
  LocationListData,
  AccountTypes,
  EditUsersList,
} from "../../types/types";
import { AxiosError } from "axios";
import LoginRestriction from "../../utils/loginRestriction";

export interface AddUserListsProps {
  openDialog: boolean;
  onCloseDialog: () => void;
  LocationClass: LocationClassList[];
  Location: LocationListData[];
  AccountType: AccountTypes[];
  editData: EditUsersList | null;
  dialogMode: "add" | "edit";
  setStatus: (status: string) => void;
}

const AddUserDialog: React.FC<AddUserListsProps> = ({
  openDialog,
  onCloseDialog,
  LocationClass,
  Location,
  AccountType,
  editData,
  dialogMode,
  setStatus,
}) => {
  const { t } = useTranslation();
  const [isRestrictionOpen, setIsRestrictionOpen] = useState(false);

  // Define state with explicit types
  const [formValues, setFormValues] = useState({
    firstName: "",
    lastName: "",
    locationClass: "",
    location: "",
    email: "",
    emailVerified: "",
    accountType: "",
    phoneNumber: "",
    username: "",
    password: "",
    businessentityId: 0,
    businessentityLocationId: 0,
    userAccessId: 0,
    classId: "", // Properly initialized as string array
  });

  const initialFormValues = {
    firstName: "",
    lastName: "",
    locationClass: "",
    location: "",
    email: "",
    emailVerified: "",
    accountType: "",
    phoneNumber: "",
    username: "",
    password: "",
    businessentityId: 0,
    businessentityLocationId: 0,
    userAccessId: 0,
    classId: "", // Array of strings,
  };

  const requiredFieldsFilled =
    formValues.firstName.trim() &&
    formValues.lastName.trim() &&
    formValues.classId &&
    formValues.email.trim() &&
    formValues.userAccessId &&
    formValues.phoneNumber.trim() &&
    formValues.username.trim() &&
    (!editData ? formValues.password.trim() !== "" : true);

  const [isEmailEnabled, setIsEmailEnabled] = useState(true);

  // Assume editData is an object containing values when editing
  useEffect(() => {
    if (editData) {
      setFormValues({
        firstName: editData.firstName || "",
        lastName: editData.lastName || "",
        phoneNumber: editData.phone || "",
        username: editData.userName || "",
        email: editData.email || "",
        locationClass: editData.businessentityId.toString() || "",
        accountType: editData.accountType || "",
        location: editData.locationName || "",
        password: "",
        emailVerified: "",
        businessentityId: editData.businessentityId || 0,
        businessentityLocationId: editData.businessentityLocationId || 0,
        userAccessId: editData.userAccessId || 0,
        classId: Array.isArray(editData.classId)
          ? editData.classId.map(String).join(",") // Convert array to comma-separated string
          : typeof editData.classId === "string"
            ? editData.classId // Convert comma-separated string to array
            : "", // Default to empty array
      });
    }
  }, [editData]); // Runs when editData changes

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleLocationClassChange = (selectedClasses: string) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      classId: selectedClasses,
    }));
  };
  const dialogClose = () => {
    setFormValues(initialFormValues);
    onCloseDialog();
  };
  const allowOnlyNumbers = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
    ];

    if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = e.clipboardData.getData("text");
    if (!/^\d+$/.test(pasteData)) {
      e.preventDefault(); // Block if pasted content is not all digits
    }
  };
  const handleDialogSubmit = async () => {
    const method = dialogMode === "add" ? "post" : "put";
    const url =
      API_BASE_URL +
      (dialogMode === "add" ? "users/adduser" : "users/update-user"); // Adjust the update endpoint if necessary

    const payload =
      dialogMode === "add"
        ? {
            webSiteId: 1,
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            phoneNumber: Number(formValues.phoneNumber),
            classId: formValues.classId,
            locationId: formValues.businessentityLocationId,
            emailId: formValues.email,
            username: formValues.username,
            password: formValues.password,
            accountAccessType: formValues.userAccessId,
            isEmailVerified: formValues.emailVerified ? 1 : 0,
          }
        : {
            userId: editData?.id,
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            phoneNumber: Number(formValues.phoneNumber),
            classId: formValues.locationClass,
            locationId: formValues.businessentityLocationId,
            emailId: formValues.email,
            userName: formValues.username,
            accountAccessType: formValues.userAccessId,
            isEmailVerified: formValues.emailVerified ? 1 : 0,
          };
    try {
      const response = await axiosInstance[method](url, payload);
      if (response.status >= 200 && response.status <= 300) {
        toast.success(response.data.data, {
          autoClose: 2000,
          position: "top-right",
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        setStatus("true");
        setFormValues(initialFormValues);
      }
    } catch (error) {
      const errCode = error as AxiosError<{ status: number }>;
      if (errCode.response && errCode.response.status === 401) {
        setIsRestrictionOpen(true);
      } else {
        toast.error("Something went wrong", {
          position: "top-right",
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
        });
      }
      throw error;
    }
    onCloseDialog();
  };

  return (
    <Box sx={{ display: "block", margin: "20px" }}>
      <Dialog open={openDialog} onClose={dialogClose} fullWidth maxWidth="md">
        <DialogTitle className="dialogtitle">
          {t("add_user")}

          <IconButton
            edge="end"
            color="inherit"
            onClick={dialogClose}
            aria-label="close"
            sx={{
              margin: "auto",
              position: "absolute",
              right: "25px",
              top: "17px",

              width: "30px",
              height: "30px",
              borderRadius: "50%",
              border: "1px solid #be1e2d", // Red border
              color: "#be1e2d", // Default text color
              "&:hover": {
                backgroundColor: "#be1e2d", // Full red background on hover
                color: "#ffffff", // White icon color
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={3} sx={{ padding: 2 }} className="addUser">
            <Grid item xs={12} md={6}>
              <FormLabel className="addUser_formlabel" required>
                {t("first_name")}
              </FormLabel>
              <Input
                name="firstName"
                size="small"
                value={formValues.firstName}
                onChange={handleInputChange}
                fullWidth
                className="addUser_input"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormLabel className="addUser_formlabel" required>
                {t("last_name")}
              </FormLabel>
              <Input
                name="lastName"
                size="small"
                value={formValues.lastName}
                onChange={handleInputChange}
                fullWidth
                className="addUser_input"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                size="small"
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              >
                <FormLabel className="addUser_formlabel">
                  {t("location_class_name")}
                </FormLabel>

                <Select
                  multiple
                  size="small"
                  value={
                    formValues.classId ? formValues.classId.split(",") : []
                  }
                  className="addUser_input"
                  onChange={(e) => {
                    const value = e.target.value;
                    const selectedClasses = Array.isArray(value)
                      ? value.map(String).join(",")
                      : String(value);
                    handleLocationClassChange(selectedClasses);
                  }}
                  input={<OutlinedInput />}
                  renderValue={
                    (selected) =>
                      selected
                        .map(
                          (id) =>
                            LocationClass.find((loc) => String(loc.id) === id)
                              ?.className
                        )
                        .filter(Boolean)
                        .join(", ") // Show class names in the dropdown
                  }
                  fullWidth
                >
                  {LocationClass.map((location) => (
                    <MenuItem key={location.id} value={String(location.id)}>
                      <Checkbox
                        checked={
                          formValues.classId
                            ?.split(",")
                            .includes(String(location.id)) ||
                          formValues.classId.includes(String(location.id))
                        }
                      />
                      <ListItemText primary={location.className} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                size="small"
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              >
                <FormLabel className="addUser_formlabel" required>
                  {t("location_name")}
                </FormLabel>
                <Select
                  name="location"
                  value={formValues.businessentityLocationId}
                  onChange={(e) =>
                    setFormValues((prevValues) => ({
                      ...prevValues,
                      businessentityLocationId: Number(e.target.value), // Store only countryCode
                    }))
                  }
                  renderValue={(selected) => {
                    const selectedLocation = Location.find(
                      (c) => c.id === Number(selected)
                    );
                    return selectedLocation ? (
                      selectedLocation.locationName
                    ) : (
                      <>{t("please_select_location")}</>
                    );
                  }}
                  size="small"
                  className="addUser_input"
                  sx={{
                    height: "36px", // Match the height of the email input
                    border: "1px solid #ccc", // Ensure border matches input field
                    borderRadius: "4px", // Consistency in border radius
                    padding: "5px", // Adjust padding to align text
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {Location.map((location) => (
                    <MenuItem key={location.id} value={Number(location.id)}>
                      {location.locationName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <FormLabel className="addUser_formlabel" required>
                  {t("email")}
                </FormLabel>
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}
                >
                  <Input
                    name="email"
                    size="small"
                    value={formValues.email}
                    onChange={handleInputChange}
                    disabled={!isEmailEnabled}
                    fullWidth
                    className="addUser_input"
                    sx={{
                      flex: 9,
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      padding: "5px",
                    }} // Adjust border and padding as needed
                  />
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "#e0e0e0", // Set background color for checkbox
                      borderRadius: "4px",
                      marginLeft: "4px", // Space between input and checkbox
                      height: "36px", // Match input height
                    }}
                  >
                    <Checkbox
                      checked={isEmailEnabled}
                      onChange={(e) => setIsEmailEnabled(e.target.checked)}
                      sx={{ padding: 0 }}
                    />
                  </Box>
                </Box>
                <FormHelperText className="addUser_formhelper">
                  {t("we_will_never_share_your_email_address")}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                size="small"
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              >
                <FormLabel className="addUser_formlabel" required>
                  {t("account_type")}
                </FormLabel>
                <Select
                  name="accountType"
                  value={formValues.userAccessId}
                  onChange={(e) =>
                    setFormValues((prevValues) => ({
                      ...prevValues,
                      userAccessId: Number(e.target.value), // Store only countryCode
                    }))
                  }
                  renderValue={(selected) => {
                    const account = AccountType.find(
                      (c) => c.userAccessId === Number(selected)
                    );
                    return account ? (
                      account.tagName
                    ) : (
                      <>{t("please_select_location")}</>
                    );
                  }}
                  size="small"
                  className="addUser_input"
                  sx={{
                    height: "36px", // Match the height of the email input
                    border: "1px solid #ccc", // Ensure border matches input field
                    borderRadius: "4px", // Consistency in border radius
                    padding: "5px", // Adjust padding to align text
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {AccountType.map((item) => (
                    <MenuItem key={item.userAccessId} value={item.userAccessId}>
                      {item.tagName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormLabel className="addUser_formlabel" required>
                {t("phone_number")}
              </FormLabel>
              <Input
                name="phoneNumber"
                size="small"
                value={formValues.phoneNumber}
                onChange={handleInputChange}
                onKeyPress={allowOnlyNumbers}
                onPaste={handlePaste}
                fullWidth
                className="addUser_input"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormLabel className="addUser_formlabel" required>
                {t("login")}
              </FormLabel>
              <Input
                name="username"
                size="small"
                value={formValues.username}
                onChange={handleInputChange}
                fullWidth
                className="addUser_input"
              />
            </Grid>
            {dialogMode === "add" && (
              <Grid item xs={12} md={6}>
                <FormLabel className="addUser_formlabel" required>
                  {t("Password")}
                </FormLabel>
                <Input
                  name="password"
                  size="small"
                  value={formValues.password}
                  onChange={handleInputChange}
                  fullWidth
                  className="addUser_input"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={dialogClose} className="cancelbtn">
            {t("cancel")}
          </Button>
          <Button
            onClick={handleDialogSubmit}
            variant="contained"
            color="primary"
            className="addbtn"
            sx={{
              cursor: !requiredFieldsFilled ? "not-allowed" : "pointer",
              opacity: !requiredFieldsFilled ? 0.9 : 1,
              pointerEvents: !requiredFieldsFilled ? "none" : "auto",
            }}
            disabled={!requiredFieldsFilled}
          >
            {dialogMode === "add" ? t("add") : t("update")}
          </Button>
        </DialogActions>
      </Dialog>
      <LoginRestriction open={isRestrictionOpen} />
    </Box>
  );
};

export default AddUserDialog;
